




import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { WeatherData } from "@/api/weather/types";
import ResizeObserver from 'resize-observer-polyfill';

@Component({
  name: "TemperatureChartHighchart",
})
export default class TemperatureChartHCharts extends Vue {
  @Prop({ type: Array, required: true })
  private averageData!: WeatherData[];

  @Prop({ type: Array, required: true })
  private historicalData!: WeatherData[];

  private chartOptions: any = {}; 

  private labels: string[] = [];
  private ro: ResizeObserver | null = null;

  @Watch("historicalData", { immediate: true })
  private historicalDataChanged(historicalData: WeatherData[]) {
    requestAnimationFrame(() => {
      this.$set(this.chartOptions, 'series', this.loadedData(this.averageData, this.historicalData));
      this.setLabels();
    });
  }

  private loadedData(averageDataParam: WeatherData[], historicalDataParam: WeatherData[]) {
    const averageData = averageDataParam.sort((x, y) => (x.weatherMonth + (x.weatherYear * 100)) - (y.weatherMonth + (y.weatherYear * 100)));
    const historicalData: WeatherData[] = [];

    averageData.forEach(x => this.historicalData.forEach(y => y.weatherMonth == x.weatherMonth ? historicalData.push(y) : ''));

    this.labels = averageData.map(weather => `${this.getMonth(weather.weatherMonth)}/${weather.weatherYear%100}`);
    return [
      {
        name: this.$t('charts.temperature.current') as string,
        data: averageData.map(weather => weather.weatherMedian),
        zIndex: 1,
        color: "#00fca1",
        marker: {
          radius: 3,
          lineColor: "#00fca1",
          fillColor: "#00fca1",
        },
        tooltip: {
          valueSuffix: 'ºC',
          valueDecimals: 1,
        },
        type: 'spline',
      },
      {
        name: this.$t('charts.temperature.historical') as string,
        data: historicalData.map(weather => weather.weatherMedian),
        color: "white",
        dashStyle: 'ShortDash',
        marker: {
          radius: 3,
          lineColor: "white",
          fillColor: "white",
        },
        tooltip: {
          valueSuffix: 'ºC',
          valueDecimals: 1,
        },
        type: 'spline',
      },
      {
        type: 'areasplinerange',
        data: this.buildRange(historicalData),
        fillOpacity: 0.3,
        linkedTo: ':previous',
        color: "white",
        lineWidth: 0,
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
      }
    ];
  }

  private buildStd(weatherDataList: WeatherData[]): Object {
    let st :any = {};
    weatherDataList.forEach(it => st[this.getMonth(it.weatherMonth)] = { plus: it.weatherStd, minus: it.weatherStd });
    return st;
  }
    
  private buildRange(weatherDataList: WeatherData[]): Object {
    return weatherDataList.map(it => [
      it.weatherMedian - 3*it.weatherStd,
      it.weatherMedian + 3*it.weatherStd
    ]);
  }

  private getMonth(month: number): string {
      return this.$t(`charts.months.${month}`) as string;
  }

  private setLabels() {
    this.$set(this.chartOptions.xAxis[0], 'categories', this.labels);
  }

  private redrawChartOnWillAppear() {
    let lastHeight = 0;
    let lastWidth = 0;

    this.ro = new ResizeObserver((entries, observer) => {
      for (const entry of entries) {
        const { height, width} = entry.contentRect;
        if ((height !== lastHeight  || width !== lastWidth)) {
          this.$nextTick(() => {
            this.reflow();
          });
        }
        lastHeight = height;
        lastWidth = width
      }
    });
    this.ro.observe((this.$refs.hcComponent! as Vue).$el);
  }

  public reflow() {
    // @ts-ignore
    this.$refs.hcComponent?.chart?.reflow();
  }

  private mounted() {
    this.chartOptions = {
      chart: {
        style: {
          fontFamily: 'BlinkMacSystemFont,-apple-system,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,Arial,sans-serif'
        },
        backgroundColor: '#12191c',
      },
      credits: {
        enabled: false,
      },
      navigation: {
        menuStyle: {
          'font-family': 'BlinkMacSystemFont,-apple-system,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,Arial,sans-serif',
        }
      },
      title: {
        text: this.$t('charts.temperature.name') as string,
        style: {
          fontSize: '12px',
          fontWeight: 'bold'
        }
      },
      xAxis: [{
        categories: [],
      }],
      yAxis: [{
        title: {
          enabled: false,
        },
        softMax: 42.0,
        softMin: 28.0,
      }],
      tooltip: {
        crosshairs: true,
        shared: true
      },
      series: this.loadedData(this.averageData, this.historicalData),
    }

    this.setLabels();
    this.redrawChartOnWillAppear();
  }
}
